import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { DialogFrame } from "@/components/common/DialogFrame";
import { IconButton } from "@/components/common/IconButton";
import { InputField } from "@/components/common/InputField";
import { Spinner } from "@/components/common/Spinner";
import { dateFormatter } from "@/lib/format-datetime";
import {
	MfaReauthRequiredError,
	completeTotpEnrollment,
	deletePasskey,
	disableTotp,
	getAccountMfa,
	reauthWithPasskey,
	reauthWithPassword,
	reauthWithRecoveryCode,
	reauthWithTotp,
	registerPasskey,
	regenerateRecoveryCodes,
	startTotpEnrollment,
	type AccountMfa,
	type RecoveryCodes,
	type RegisteredPasskey,
} from "@/lib/mfa-api";
import { passkeySupportedInBrowser, signalAcceptedPasskeys } from "@/lib/webauthn";
import { MfaVerifyForm, type MfaVerifier } from "./MfaVerifyForm";
import { RecoveryCodesPanel } from "./RecoveryCodesPanel";
import { TotpSetupForm } from "./TotpSetupForm";

const reauthVerifier: MfaVerifier = {
	totp: reauthWithTotp,
	recoveryCode: reauthWithRecoveryCode,
	passkey: reauthWithPasskey,
};

type PendingConfirm =
	| { kind: "disableTotp" }
	| { kind: "deletePasskey"; passkey: RegisteredPasskey }
	| { kind: "regenerateCodes" };

/** 自分の多要素認証の手段 (認証アプリ・パスキー・リカバリーコード) を確認・変更する。 */
export function AccountSecurityDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
	const [mfa, setMfa] = useState<AccountMfa | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	const [view, setView] = useState<"overview" | "reauth" | "totp">("overview");
	const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
	const [registeringPasskey, setRegisteringPasskey] = useState(false);
	const [pending, setPending] = useState<PendingConfirm | null>(null);
	const [browserSupportsPasskey, setBrowserSupportsPasskey] = useState(false);

	const reload = useCallback(async () => {
		const next = await getAccountMfa();
		setMfa(next);
		if (next.rpId)
			signalAcceptedPasskeys(
				next.rpId,
				next.userHandle,
				next.passkeys.map((p) => p.credentialId),
			).catch((err: unknown) =>
				console.error("登録済みのパスキーをパスワードマネージャーに知らせられませんでした", err),
			);
	}, []);

	useEffect(() => {
		if (!open) return;
		setView("overview");
		setRecoveryCodes(null);
		setActionError(null);
		setLoadError(null);
		reload().catch((err: unknown) => {
			console.error("多要素認証の設定を取得できませんでした", err);
			setLoadError(err instanceof Error ? err.message : "多要素認証の設定を取得できませんでした。");
		});
		passkeySupportedInBrowser()
			.then(setBrowserSupportsPasskey)
			.catch((err: unknown) => console.error("パスキーの対応状況を確認できませんでした", err));
	}, [open, reload]);

	/** 変更は済んでいるので、読み込み直しの失敗は変更の失敗として扱わず、ここで表示する。 */
	async function reloadAfterChange() {
		try {
			await reload();
		} catch (err) {
			console.error("多要素認証の設定を読み込み直せませんでした", err);
			setActionError("変更しましたが、最新の設定を読み込めませんでした。開き直してください。");
		}
	}

	/** 本人確認の期限が切れていれば確認画面に戻して true を返す。 */
	const reauthIfRequired = useCallback((err: unknown) => {
		if (!(err instanceof MfaReauthRequiredError)) return false;
		setPending(null);
		setMfa((current) => current && { ...current, reauthenticated: false });
		setView("reauth");
		return true;
	}, []);

	/** 変更の前に本人確認し直していなければ、先に確認画面を出す。 */
	function ensureReauthenticated(): boolean {
		if (mfa?.reauthenticated) return true;
		setActionError(null);
		setView("reauth");
		return false;
	}

	async function afterReauthentication() {
		setView("overview");
		await reloadAfterChange();
	}

	async function afterEnrollment(codes: RecoveryCodes) {
		setView("overview");
		if (codes && codes.length > 0) setRecoveryCodes(codes);
		await reloadAfterChange();
	}

	async function handleAddPasskey() {
		if (!ensureReauthenticated()) return;
		setActionError(null);
		setRegisteringPasskey(true);
		try {
			const codes = await registerPasskey("");
			if (codes !== undefined) await afterEnrollment(codes);
		} catch (err) {
			if (reauthIfRequired(err)) return;
			setActionError(err instanceof Error ? err.message : "パスキーを登録できませんでした。");
		} finally {
			setRegisteringPasskey(false);
		}
	}

	function confirm(next: PendingConfirm) {
		if (ensureReauthenticated()) setPending(next);
	}

	async function handleConfirm() {
		if (!pending) return;
		try {
			if (pending.kind === "disableTotp") await disableTotp();
			else if (pending.kind === "deletePasskey") await deletePasskey(pending.passkey.id);
			else setRecoveryCodes(await regenerateRecoveryCodes());
		} catch (err) {
			if (reauthIfRequired(err)) return;
			throw err;
		}
		setPending(null);
		await reloadAfterChange();
	}

	const hasFactor = mfa !== null && (mfa.totpEnabled || mfa.passkeys.length > 0);
	const passkeyUsable = mfa?.passkeyAvailable === true && browserSupportsPasskey;
	// リカバリーコードは閉じると再表示できないため、「保存しました」でだけ先へ進ませる。
	const showingRecoveryCodes = recoveryCodes !== null;

	return (
		<DialogFrame
			open={open}
			title="多要素認証"
			width={560}
			onClose={showingRecoveryCodes ? () => {} : onClose}
			footer={
				showingRecoveryCodes ? null : (
					<ConfirmButton label="閉じる" variant="ghost" loading={false} onClick={onClose} />
				)
			}
		>
			{loadError ? (
				<p style={{ margin: 0, color: "var(--danger)", fontSize: "var(--text-ui)" }}>{loadError}</p>
			) : mfa === null ? (
				<Spinner size={14} />
			) : recoveryCodes ? (
				<RecoveryCodesPanel codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
			) : view === "reauth" ? (
				<ReauthStep
					mfa={mfa}
					passkeyUsable={passkeyUsable}
					onVerified={() => void afterReauthentication()}
					onCancel={() => setView("overview")}
				/>
			) : view === "totp" ? (
				<TotpSetupForm
					start={startTotpEnrollment}
					complete={completeTotpEnrollment}
					onDone={(codes) => void afterEnrollment(codes)}
					onFatalError={reauthIfRequired}
					onCancel={() => setView("overview")}
				/>
			) : (
				<div
					data-testid="account-mfa"
					style={{ display: "flex", flexDirection: "column", gap: 20 }}
				>
					<p
						style={{
							margin: 0,
							fontSize: "var(--text-ui)",
							color: "var(--fg-2)",
							lineHeight: "var(--leading-body)",
						}}
					>
						{mfa.required
							? "組織の設定により、多要素認証は必須です。サインインのたびに、パスワードに加えて以下のいずれかで本人確認します。"
							: "設定すると、サインインのたびにパスワードに加えて以下のいずれかで本人確認します。パスワードが漏れても、アカウントを守れます。"}
					</p>

					<Section
						title="パスキー"
						description="端末の指紋・顔認証や画面ロック、セキュリティキーで確認します。フィッシングに強く、最もおすすめの手段です。"
						action={
							passkeyUsable && (
								<ConfirmButton
									label="パスキーを追加"
									size="sm"
									variant="secondary"
									loading={registeringPasskey}
									onClick={() => void handleAddPasskey()}
									data-testid="account-mfa-add-passkey"
								/>
							)
						}
					>
						{!mfa.passkeyAvailable ? (
							<Note>この環境ではパスキーを使えません。認証アプリをお使いください。</Note>
						) : !browserSupportsPasskey ? (
							<Note>このブラウザではパスキーを使えません。</Note>
						) : mfa.passkeys.length === 0 ? (
							<Note>登録されたパスキーはありません。</Note>
						) : (
							mfa.passkeys.map((passkey) => (
								<PasskeyRow
									key={passkey.id}
									passkey={passkey}
									onDelete={() => confirm({ kind: "deletePasskey", passkey })}
								/>
							))
						)}
					</Section>

					<Section
						title="認証アプリ"
						description="Google Authenticator などのアプリに表示される 6 桁のコードで確認します。"
						action={
							mfa.totpEnabled ? (
								<ConfirmButton
									label="削除"
									size="sm"
									variant="secondary"
									loading={false}
									onClick={() => confirm({ kind: "disableTotp" })}
									data-testid="account-mfa-disable-totp"
								/>
							) : (
								<ConfirmButton
									label="設定"
									size="sm"
									variant="secondary"
									loading={false}
									onClick={() => {
										if (ensureReauthenticated()) setView("totp");
									}}
									data-testid="account-mfa-setup-totp"
								/>
							)
						}
					>
						<Note>{mfa.totpEnabled ? "設定済み" : "未設定"}</Note>
					</Section>

					{hasFactor && (
						<Section
							title="リカバリーコード"
							description="パスキーや認証アプリを使えなくなったときに、1 回だけ使えるコードです。"
							action={
								<ConfirmButton
									label="再発行"
									size="sm"
									variant="secondary"
									loading={false}
									onClick={() => confirm({ kind: "regenerateCodes" })}
									data-testid="account-mfa-regenerate-codes"
								/>
							}
						>
							<Note>残り {mfa.recoveryCodesRemaining} 個</Note>
						</Section>
					)}

					{actionError && (
						<p style={{ margin: 0, color: "var(--danger)", fontSize: "var(--text-caption)" }}>
							{actionError}
						</p>
					)}
				</div>
			)}
			<ConfirmDialog
				open={pending !== null}
				title={confirmTitle(pending)}
				message={confirmMessage(pending)}
				confirmLabel={pending?.kind === "regenerateCodes" ? "再発行" : "削除"}
				onConfirm={handleConfirm}
				onCancel={() => setPending(null)}
			/>
		</DialogFrame>
	);
}

/** 手段を変える前の本人確認。登録済みの手段があればそれで、まだなければパスワードで確認する。 */
function ReauthStep({
	mfa,
	passkeyUsable,
	onVerified,
	onCancel,
}: {
	mfa: AccountMfa;
	passkeyUsable: boolean;
	onVerified: () => void;
	onCancel: () => void;
}) {
	const hasFactor = mfa.totpEnabled || mfa.passkeys.length > 0;
	return (
		<div
			data-testid="account-mfa-reauth"
			style={{ display: "flex", flexDirection: "column", gap: 16 }}
		>
			<p
				style={{
					margin: 0,
					fontSize: "var(--text-ui)",
					color: "var(--fg-2)",
					lineHeight: "var(--leading-body)",
				}}
			>
				{hasFactor
					? "多要素認証の設定を変更するには、登録済みの手段で本人確認してください。"
					: "多要素認証の設定を変更するには、パスワードを入力して本人確認してください。"}
			</p>
			{hasFactor ? (
				<MfaVerifyForm
					methods={{
						totp: mfa.totpEnabled,
						passkey: mfa.passkeys.length > 0,
						recoveryCode: mfa.recoveryCodesRemaining > 0,
					}}
					passkeyUsable={mfa.passkeys.length > 0 && passkeyUsable}
					verifier={reauthVerifier}
					onVerified={onVerified}
				/>
			) : (
				<PasswordReauthForm onVerified={onVerified} />
			)}
			<div style={{ alignSelf: "flex-start" }}>
				<ConfirmButton label="戻る" variant="ghost" loading={false} onClick={onCancel} />
			</div>
		</div>
	);
}

function PasswordReauthForm({ onVerified }: { onVerified: () => void }) {
	const [password, setPassword] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setSubmitting(true);
		setError(null);
		try {
			await reauthWithPassword(password);
			onVerified();
		} catch (err) {
			setError(err instanceof Error ? err.message : "パスワードを確認できませんでした。");
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<InputField label="パスワード" error={error ?? undefined}>
				<input
					type="password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					autoComplete="current-password"
					autoFocus
					data-testid="account-mfa-reauth-password"
					style={{
						height: 36,
						padding: "0 10px",
						fontSize: "var(--text-ui)",
						color: "var(--fg-1)",
						background: "var(--surface-1)",
						border: "none",
						borderRadius: "var(--radius-3)",
						boxShadow: "var(--shadow-ring)",
						outline: "none",
						fontFamily: "var(--font-sans)",
					}}
				/>
			</InputField>
			<ConfirmButton
				type="submit"
				label="確認"
				loading={submitting}
				disabled={password.length === 0}
				data-testid="account-mfa-reauth-submit"
			/>
		</form>
	);
}

function confirmTitle(pending: PendingConfirm | null): string {
	if (pending?.kind === "disableTotp") return "認証アプリを削除";
	if (pending?.kind === "deletePasskey") return "パスキーを削除";
	return "リカバリーコードを再発行";
}

function confirmMessage(pending: PendingConfirm | null): string {
	if (pending?.kind === "disableTotp") return "認証アプリのコードでサインインできなくなります。";
	if (pending?.kind === "deletePasskey")
		return `「${pending.passkey.name}」でサインインできなくなります。`;
	return "今までのリカバリーコードはすべて使えなくなります。";
}

function Section({
	title,
	description,
	action,
	children,
}: {
	title: string;
	description: string;
	action: ReactNode;
	children: ReactNode;
}) {
	return (
		<section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
				<div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
					<h4
						style={{ margin: 0, fontSize: "var(--text-ui)", fontWeight: 600, color: "var(--fg-1)" }}
					>
						{title}
					</h4>
					<p
						style={{
							margin: 0,
							fontSize: "var(--text-caption)",
							color: "var(--fg-3)",
							lineHeight: "var(--leading-ui)",
						}}
					>
						{description}
					</p>
				</div>
				{action}
			</div>
			{children}
		</section>
	);
}

function Note({ children }: { children: ReactNode }) {
	return <span style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>{children}</span>;
}

function PasskeyRow({ passkey, onDelete }: { passkey: RegisteredPasskey; onDelete: () => void }) {
	return (
		<div
			data-testid="account-mfa-passkey"
			style={{
				display: "flex",
				alignItems: "center",
				gap: 12,
				padding: "8px 12px",
				borderRadius: "var(--radius-3)",
				boxShadow: "var(--shadow-ring)",
			}}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
				<span style={{ fontSize: "var(--text-ui)", color: "var(--fg-1)" }}>{passkey.name}</span>
				<span style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
					登録 {dateFormatter.format(new Date(passkey.createdAt))}
					{passkey.lastUsedAt &&
						` ・ 最終使用 ${dateFormatter.format(new Date(passkey.lastUsedAt))}`}
				</span>
			</div>
			<IconButton icon="trash" size={28} onClick={onDelete} />
		</div>
	);
}
